/**
 * エラーコードとユーザーフレンドリーなメッセージのマッピング
 * Backend (Go) の app_error/error_codes.go と同期すること
 */
import { Code } from '@connectrpc/connect'

export type ErrorCode = string

// エラーコードの定義
export const ERROR_CODES = {
  // ===== 案件 (Deal) 関連 =====
  DEAL_DUPLICATE_EXTERNAL_ID: 'DEAL_DUPLICATE_EXTERNAL_ID',
  DEAL_NOT_FOUND: 'DEAL_NOT_FOUND',
  DEAL_INVALID_STATE: 'DEAL_INVALID_STATE',
  DEAL_PERMISSION_DENIED: 'DEAL_PERMISSION_DENIED',
  // presigned PUT の実体が無い / 申告サイズと違う（issue #2647）
  DEAL_FILE_UPLOAD_INCOMPLETE: 'DEAL_FILE_UPLOAD_INCOMPLETE',

  // ===== 顧客 (Customer) 関連 =====
  CUSTOMER_NOT_FOUND: 'CUSTOMER_NOT_FOUND',
  CUSTOMER_DUPLICATE_EMAIL: 'CUSTOMER_DUPLICATE_EMAIL',
  CUSTOMER_DUPLICATE_PHONE: 'CUSTOMER_DUPLICATE_PHONE',
  CUSTOMER_PERMISSION_DENIED: 'CUSTOMER_PERMISSION_DENIED',

  // ===== 会社 (Company) 関連 =====
  COMPANY_NOT_FOUND: 'COMPANY_NOT_FOUND',
  COMPANY_PERMISSION_DENIED: 'COMPANY_PERMISSION_DENIED',

  // ===== スタッフ (Staff) 関連 =====
  STAFF_NOT_FOUND: 'STAFF_NOT_FOUND',
  STAFF_DUPLICATE_EMAIL: 'STAFF_DUPLICATE_EMAIL',
  STAFF_PERMISSION_DENIED: 'STAFF_PERMISSION_DENIED',

  // ===== 見積書・請求書 (Paperwork) 関連 =====
  QUOTATION_PERMISSION_DENIED: 'QUOTATION_PERMISSION_DENIED',
  INVOICE_PERMISSION_DENIED: 'INVOICE_PERMISSION_DENIED',

  // ===== 管理者 (Admin) 関連 =====
  ADMIN_NOT_FOUND: 'ADMIN_NOT_FOUND',
  ADMIN_CANNOT_DELETE_SELF: 'ADMIN_CANNOT_DELETE_SELF',
  ADMIN_CANNOT_CHANGE_OWN_ROLE: 'ADMIN_CANNOT_CHANGE_OWN_ROLE',

  // Admin 承認ワークフロー関連
  APPROVAL_NOT_FOUND: 'APPROVAL_NOT_FOUND',
  APPROVAL_SELF_NOT_ALLOWED: 'APPROVAL_SELF_NOT_ALLOWED',
  APPROVAL_NOT_PENDING: 'APPROVAL_NOT_PENDING',
  APPROVAL_NOT_APPROVED: 'APPROVAL_NOT_APPROVED',

  // ===== バリデーション関連 =====
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  VALIDATION_INVALID_EMAIL: 'VALIDATION_INVALID_EMAIL',
  VALIDATION_INVALID_PHONE: 'VALIDATION_INVALID_PHONE',
  VALIDATION_REQUIRED: 'VALIDATION_REQUIRED',
  VALIDATION_TOO_LONG: 'VALIDATION_TOO_LONG',
  VALIDATION_ROUNDING_MISMATCH: 'VALIDATION_ROUNDING_MISMATCH',

  // ===== 認証・認可関連 =====
  AUTHENTICATION_FAILED: 'AUTHENTICATION_FAILED',
  AUTHORIZATION_FAILED: 'AUTHORIZATION_FAILED',
  SESSION_EXPIRED: 'SESSION_EXPIRED',
  INVALID_TOKEN: 'INVALID_TOKEN',
  /**
   * パスワード設定リンクのトークンが見つからない。
   * 設定完了後の再送信・古いリンク・二重送信で到達する想定内の状態であり、
   * アプリのバグではないため Sentry へは送らない（`SENTRY_EXPECTED_APP_ERROR_CODES` 参照）。
   */
  PASSWORD_RESET_LINK_INVALID: 'PASSWORD_RESET_LINK_INVALID',
  /**
   * 停止済み（archived）のスタッフが**認証で**弾かれた。
   * サインインは通っている＝本人と確認できているので、理由をそのまま画面に出してよい。
   * `STAFF_ARCHIVED`（停止済みスタッフを操作対象にした業務エラー）とは別物。
   *
   * 企業が停止したアカウントで必ず出る想定内の状態であり、アプリのバグではないため
   * Sentryへは送らない（`SENTRY_EXPECTED_APP_ERROR_CODES` 参照）。
   */
  STAFF_ACCOUNT_DISABLED: 'STAFF_ACCOUNT_DISABLED',
  SUPPORT_ACCESS_DISABLED: 'SUPPORT_ACCESS_DISABLED',

  // ===== データベース関連 =====
  DB_DUPLICATE_ENTRY: 'DB_DUPLICATE_ENTRY',
  DB_FOREIGN_KEY_VIOLATION: 'DB_FOREIGN_KEY_VIOLATION',
  DB_DATA_TOO_LONG: 'DB_DATA_TOO_LONG',
  DB_CONNECTION_FAILED: 'DB_CONNECTION_FAILED',

  // ===== AIカラープロジェクト関連 =====
  AI_COLOR_PROJECT_LINKED_TO_DEAL: 'AI_COLOR_PROJECT_LINKED_TO_DEAL',
  AI_COLOR_PIN_LIMIT_EXCEEDED: 'AI_COLOR_PIN_LIMIT_EXCEEDED',

  // ===== AIカラー色プリセット関連 =====
  COLOR_PRESET_IN_USE: 'COLOR_PRESET_IN_USE',

  // ===== AIカラー 共有リンク（施主）関連 =====
  SHARED_AREA_NOT_EDITABLE: 'SHARED_AREA_NOT_EDITABLE',
  SHARED_HISTORY_TITLE_NOT_EDITABLE: 'SHARED_HISTORY_TITLE_NOT_EDITABLE',
  SHARED_COLOR_NOT_ALLOWED: 'SHARED_COLOR_NOT_ALLOWED',
  SHARED_IMAGE_NOT_FOUND: 'SHARED_IMAGE_NOT_FOUND',
  SHARE_LINK_UNAVAILABLE: 'SHARE_LINK_UNAVAILABLE',

  // ===== メディアサイト CMS 関連 =====
  MEDIA_CATEGORY_IN_USE: 'MEDIA_CATEGORY_IN_USE',
  MEDIA_TAG_IN_USE: 'MEDIA_TAG_IN_USE',
  MEDIA_SLUG_NOT_FOUND: 'MEDIA_SLUG_NOT_FOUND',
  MEDIA_ROOFER_SLUG_INVALID: 'MEDIA_ROOFER_SLUG_INVALID',
  MEDIA_ROOFER_SLUG_TAKEN: 'MEDIA_ROOFER_SLUG_TAKEN',
  MEDIA_ROOFER_REVIEW_INVALID: 'MEDIA_ROOFER_REVIEW_INVALID',
  MEDIA_ROOFER_CRAFTSMAN_NAME_REQUIRED: 'MEDIA_ROOFER_CRAFTSMAN_NAME_REQUIRED',
  MEDIA_ROOFER_CASE_URL_INVALID: 'MEDIA_ROOFER_CASE_URL_INVALID',
  MEDIA_ROOFER_NOT_FOUND: 'MEDIA_ROOFER_NOT_FOUND',
  MEDIA_CREDENTIAL_IN_USE: 'MEDIA_CREDENTIAL_IN_USE',
  MEDIA_IMAGE_NOT_FOUND: 'MEDIA_IMAGE_NOT_FOUND',
  MEDIA_SLUG_INVALID: 'MEDIA_SLUG_INVALID',
  MEDIA_SLUG_RESERVED: 'MEDIA_SLUG_RESERVED',
  MEDIA_ROOFER_PLACE_ID_REQUIRED: 'MEDIA_ROOFER_PLACE_ID_REQUIRED',
  MEDIA_ROOFER_PLACE_ID_INVALID: 'MEDIA_ROOFER_PLACE_ID_INVALID',
  MEDIA_ROOFER_COMPANY_LINK_REQUIRED: 'MEDIA_ROOFER_COMPANY_LINK_REQUIRED',
  MEDIA_ROOFER_CATEGORY_REQUIRED: 'MEDIA_ROOFER_CATEGORY_REQUIRED',
  MEDIA_ROOFER_FIELD_NOT_UPDATABLE: 'MEDIA_ROOFER_FIELD_NOT_UPDATABLE',
  MEDIA_ROOFER_IMAGE_URL_UNKNOWN: 'MEDIA_ROOFER_IMAGE_URL_UNKNOWN',
  MEDIA_ROOFER_NAME_REQUIRED: 'MEDIA_ROOFER_NAME_REQUIRED',
  MEDIA_ROOFER_SLUG_LOCKED: 'MEDIA_ROOFER_SLUG_LOCKED',
  MEDIA_ROOFER_CATEGORY_LOCKED: 'MEDIA_ROOFER_CATEGORY_LOCKED',
  MEDIA_ROOFER_COMPANY_NOT_FOUND: 'MEDIA_ROOFER_COMPANY_NOT_FOUND',
  MEDIA_ROOFER_INTERVIEW_NOT_FOUND: 'MEDIA_ROOFER_INTERVIEW_NOT_FOUND',
  MEDIA_ROOFER_FEATURED_REVIEW_UNKNOWN: 'MEDIA_ROOFER_FEATURED_REVIEW_UNKNOWN',

  // ===== ナレッジベース管理関連 =====
  KNOWLEDGE_SECTION_CONFLICT: 'KNOWLEDGE_SECTION_CONFLICT',
  KNOWLEDGE_SECTION_ORDER_CONFLICT: 'KNOWLEDGE_SECTION_ORDER_CONFLICT',

  // ===== AI 再実行の回数制限関連 =====
  MASK_REGENERATION_LIMIT_EXCEEDED: 'MASK_REGENERATION_LIMIT_EXCEEDED',
  AREA_REANALYZE_LIMIT_EXCEEDED: 'AREA_REANALYZE_LIMIT_EXCEEDED',

  // ===== 契約管理（Admin・新体系）関連 =====
  CONTRACT_NOT_FOUND: 'CONTRACT_NOT_FOUND',
  CONTRACT_ACTIVE_EXISTS: 'CONTRACT_ACTIVE_EXISTS',
  CONTRACT_NOT_ACTIVE: 'CONTRACT_NOT_ACTIVE',
  CONTRACT_PLAN_TYPE_MISMATCH: 'CONTRACT_PLAN_TYPE_MISMATCH',
  CONTRACT_PLAN_UNCHANGED: 'CONTRACT_PLAN_UNCHANGED',
  PLAN_NOT_AVAILABLE: 'PLAN_NOT_AVAILABLE',
  STAFF_LIMIT_EXCEEDED: 'STAFF_LIMIT_EXCEEDED',
  STAFF_ARCHIVED: 'STAFF_ARCHIVED',
  /** 停止済みを前提とする操作（担当の引き継ぎ・匿名化）を有効なスタッフに呼んだ。STAFF_ARCHIVEDの逆向き */
  STAFF_NOT_ARCHIVED: 'STAFF_NOT_ARCHIVED',
  /** 匿名化済み（不可逆）のスタッフを停止解除しようとした */
  STAFF_ANONYMIZED: 'STAFF_ANONYMIZED',
  SUPPORT_STAFF_EMAIL_INVALID: 'SUPPORT_STAFF_EMAIL_INVALID',
  STAFF_NOT_SUPPORT: 'STAFF_NOT_SUPPORT',
  STAFF_PLAN_ASSIGNMENT_INVALID: 'STAFF_PLAN_ASSIGNMENT_INVALID',

  // ===== プラン申し込み（企業画面）関連 =====
  /**
   * プラン申し込みダイアログの連投（同一スタッフ1分1回）。
   * ユーザーの二度押しで必ず出る想定内の状態なのでSentryへは送らない
   * （`SENTRY_EXPECTED_APP_ERROR_CODES` 参照）。
   */
  PLAN_INQUIRY_RATE_LIMITED: 'PLAN_INQUIRY_RATE_LIMITED',

  // ===== クレジット台帳関連 =====
  /**
   * クレジットを消費するAI3機能（AIカラー・積算・現地調査報告書）の実行前の枠押さえで
   * 残高が足りない。契約中でも残高が尽きれば必ず出る想定内の状態なのでSentryへは送らない
   * （`SENTRY_EXPECTED_APP_ERROR_CODES` 参照）。
   */
  INSUFFICIENT_CREDIT: 'INSUFFICIENT_CREDIT',

  // ===== クレジット台帳（枠押さえ・確定・解放の失敗の区分。決定104）関連 =====
  /**
   * 冪等キーが別内容の実行で既に使われている（区分B2）。画面の再送・二重送信で起きうる。
   */
  CREDIT_REQUEST_CONFLICT: 'CREDIT_REQUEST_CONFLICT',
  /**
   * 冪等キーに対応する枠が既に解放済み（区分B3）。
   */
  CREDIT_REQUEST_CANCELED: 'CREDIT_REQUEST_CANCELED',
  /**
   * ロック待ち超過・基盤障害（区分C2・D）。時間をおいた再試行で解消する想定。
   */
  CREDIT_LEDGER_BUSY: 'CREDIT_LEDGER_BUSY',

  // ===== LINE ログイン・連携関連 =====
  LINE_NOT_LINKED: 'LINE_NOT_LINKED',
  LINE_ALREADY_LINKED_OTHER: 'LINE_ALREADY_LINKED_OTHER',
  LINE_LOGIN_STATE_INVALID: 'LINE_LOGIN_STATE_INVALID',
  LINE_LOGIN_SESSION_EXPIRED: 'LINE_LOGIN_SESSION_EXPIRED',
  LINE_API_ERROR: 'LINE_API_ERROR',
  LINE_LOGIN_RATE_LIMITED: 'LINE_LOGIN_RATE_LIMITED',
  LINE_LOGIN_CRM_REQUIRED: 'LINE_LOGIN_CRM_REQUIRED',
  STAFF_INACTIVE: 'STAFF_INACTIVE',

  // ===== 流量制限 =====
  // 認証済みの主体（スタッフ・管理者・共有リンク）ごとの流量制限を超えた（HTTP 429）。
  // Go 側は app_middleware.RateLimitInterceptor。metadata.retry_after に再試行までの秒数が入る。
  RATE_LIMITED: 'RATE_LIMITED',

  // ===== システムエラー =====
  INTERNAL_SERVER_ERROR: 'INTERNAL_SERVER_ERROR',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
  EXTERNAL_SERVICE_ERROR: 'EXTERNAL_SERVICE_ERROR',
} as const

// エラーコードとメッセージのマッピング
export const ERROR_MESSAGES: Record<keyof typeof ERROR_CODES, string> = {
  // 案件関連
  [ERROR_CODES.DEAL_DUPLICATE_EXTERNAL_ID]: '既に同じ外部IDが登録されています',
  [ERROR_CODES.DEAL_NOT_FOUND]: '案件が見つかりません',
  [ERROR_CODES.DEAL_INVALID_STATE]: '案件の状態が不正です',
  [ERROR_CODES.DEAL_PERMISSION_DENIED]: 'この案件にアクセスする権限がありません',
  [ERROR_CODES.DEAL_FILE_UPLOAD_INCOMPLETE]:
    'ファイルのアップロードが完了していません。もう一度お試しください',

  // 顧客関連
  [ERROR_CODES.CUSTOMER_NOT_FOUND]: 'お客様が見つかりません',
  [ERROR_CODES.CUSTOMER_DUPLICATE_EMAIL]: 'このメールアドレスは既に登録されています',
  [ERROR_CODES.CUSTOMER_DUPLICATE_PHONE]: 'この電話番号は既に登録されています',
  [ERROR_CODES.CUSTOMER_PERMISSION_DENIED]: 'このお客様にアクセスする権限がありません',

  // 会社関連
  [ERROR_CODES.COMPANY_NOT_FOUND]: '会社が見つかりません',
  [ERROR_CODES.COMPANY_PERMISSION_DENIED]: 'この会社にアクセスする権限がありません',

  // スタッフ関連
  [ERROR_CODES.STAFF_NOT_FOUND]: 'スタッフが見つかりません',
  [ERROR_CODES.STAFF_DUPLICATE_EMAIL]: 'このメールアドレスは既に登録されています',
  [ERROR_CODES.STAFF_PERMISSION_DENIED]: 'このスタッフにアクセスする権限がありません',

  // 見積書・請求書関連
  [ERROR_CODES.QUOTATION_PERMISSION_DENIED]: 'この見積書にアクセスする権限がありません',
  [ERROR_CODES.INVOICE_PERMISSION_DENIED]: 'この請求書にアクセスする権限がありません',

  // 管理者関連
  [ERROR_CODES.ADMIN_NOT_FOUND]: '管理者が見つかりません',
  [ERROR_CODES.ADMIN_CANNOT_DELETE_SELF]: '自分自身は削除できません',
  [ERROR_CODES.ADMIN_CANNOT_CHANGE_OWN_ROLE]: '自分自身のロールは変更できません',
  [ERROR_CODES.APPROVAL_NOT_FOUND]: '対象の申請が見つかりません',
  [ERROR_CODES.APPROVAL_SELF_NOT_ALLOWED]: '自分自身の申請は承認・却下できません',
  [ERROR_CODES.APPROVAL_NOT_PENDING]:
    '対象の申請は承認待ちではありません。画面を再読み込みしてお試しください',
  [ERROR_CODES.APPROVAL_NOT_APPROVED]: '対象の申請は承認されていません',

  // バリデーション関連
  [ERROR_CODES.VALIDATION_FAILED]: '入力した値が不正です',
  [ERROR_CODES.VALIDATION_INVALID_EMAIL]: 'メールアドレスの形式が正しくありません',
  [ERROR_CODES.VALIDATION_INVALID_PHONE]: '電話番号の形式が正しくありません',
  [ERROR_CODES.VALIDATION_REQUIRED]: '必須項目が入力されていません',
  [ERROR_CODES.VALIDATION_TOO_LONG]: '入力値が長すぎます',
  [ERROR_CODES.VALIDATION_ROUNDING_MISMATCH]:
    '端数調整の税抜・税込金額が整合していません。税込金額を再入力してください',

  // 認証・認可関連
  [ERROR_CODES.AUTHENTICATION_FAILED]: '認証に失敗しました',
  [ERROR_CODES.AUTHORIZATION_FAILED]: '権限がありません',
  [ERROR_CODES.SESSION_EXPIRED]: 'セッションの有効期限が切れました。再度ログインしてください',
  [ERROR_CODES.INVALID_TOKEN]: 'トークンが無効です',
  [ERROR_CODES.PASSWORD_RESET_LINK_INVALID]:
    'このリンクは無効です。使用済みか、無効になっている可能性があります。「設定リンクを再送する」から新しいリンクを取得してください。',
  [ERROR_CODES.STAFF_ACCOUNT_DISABLED]:
    'このアカウントは無効化されています。企業の管理者にお問い合わせください',
  [ERROR_CODES.SUPPORT_ACCESS_DISABLED]:
    'サポートアカウントのログインが有効になっていません。管理画面でログインを有効にしてください',

  // データベース関連
  [ERROR_CODES.DB_DUPLICATE_ENTRY]: '既に同じ値が登録されています',
  [ERROR_CODES.DB_FOREIGN_KEY_VIOLATION]: 'このデータは他で使用されているため削除できません',
  [ERROR_CODES.DB_DATA_TOO_LONG]: '入力値が長すぎます',
  [ERROR_CODES.DB_CONNECTION_FAILED]: 'データベースへの接続に失敗しました',

  // AIカラープロジェクト関連
  [ERROR_CODES.AI_COLOR_PROJECT_LINKED_TO_DEAL]:
    '案件連携中は顧客を変更できません。先に案件連携を解除してください',
  [ERROR_CODES.AI_COLOR_PIN_LIMIT_EXCEEDED]:
    'ピン留めは1プロジェクトに4件までです。外す履歴を選んでから、もう一度お試しください',

  // AIカラー色プリセット関連
  [ERROR_CODES.COLOR_PRESET_IN_USE]:
    'このプリセットはエリアの初期表示に設定されているため削除できません',

  // AIカラー 共有リンク（施主）関連
  [ERROR_CODES.SHARED_AREA_NOT_EDITABLE]:
    'この画像で変更できないエリアが含まれています。画面を再読み込みしてお試しください',
  [ERROR_CODES.SHARED_COLOR_NOT_ALLOWED]:
    '選択できない色が含まれています。画面を再読み込みしてお試しください',
  [ERROR_CODES.SHARED_HISTORY_TITLE_NOT_EDITABLE]: 'お客様が実行した履歴のタイトルだけ変更できます',
  [ERROR_CODES.SHARED_IMAGE_NOT_FOUND]:
    '指定された画像が見つかりません。画面を再読み込みしてお試しください',
  [ERROR_CODES.SHARE_LINK_UNAVAILABLE]: 'このリンクは現在ご利用いただけません',

  // メディアサイト CMS 関連
  [ERROR_CODES.MEDIA_CATEGORY_IN_USE]: 'このカテゴリは記事で使用されているため削除できません',
  [ERROR_CODES.MEDIA_TAG_IN_USE]: 'このタグは記事で使用されているため削除できません',
  [ERROR_CODES.MEDIA_SLUG_NOT_FOUND]: '指定された記事・インタビューが見つかりません',
  [ERROR_CODES.MEDIA_ROOFER_SLUG_INVALID]: 'slug は半角英小文字・数字・ハイフンのみ使用できます',
  [ERROR_CODES.MEDIA_ROOFER_SLUG_TAKEN]: 'この slug は他の認定店で使用されています',
  [ERROR_CODES.MEDIA_ROOFER_REVIEW_INVALID]:
    '口コミ埋め込みコードから有効な Google 埋め込み URL を取得できませんでした',
  [ERROR_CODES.MEDIA_ROOFER_CRAFTSMAN_NAME_REQUIRED]: '職人の氏名は必須です',
  [ERROR_CODES.MEDIA_ROOFER_CASE_URL_INVALID]: '施工事例記事の URL が不正です（https のみ）',
  [ERROR_CODES.MEDIA_ROOFER_NOT_FOUND]: '指定された認定店が見つかりません',
  [ERROR_CODES.MEDIA_CREDENTIAL_IN_USE]: 'この項目は認定店にアサインされているため削除できません',
  [ERROR_CODES.MEDIA_IMAGE_NOT_FOUND]: '指定された画像が見つかりません',
  [ERROR_CODES.MEDIA_SLUG_INVALID]: 'slug は半角英小文字・数字・ハイフンのみ使用できます',
  [ERROR_CODES.MEDIA_SLUG_RESERVED]: 'この slug は予約されているため使用できません',
  [ERROR_CODES.MEDIA_ROOFER_PLACE_ID_REQUIRED]: 'Google Place ID を入力してください',
  [ERROR_CODES.MEDIA_ROOFER_PLACE_ID_INVALID]:
    'Google Place ID が正しくありません。Place ID をご確認ください',
  [ERROR_CODES.MEDIA_ROOFER_COMPANY_LINK_REQUIRED]:
    'いえサプリ企業と連携されていません。認定店にいえサプリ企業を設定してください',
  [ERROR_CODES.MEDIA_ROOFER_CATEGORY_REQUIRED]:
    '認定店の分類を 1 つ以上選択してください（画面が古い場合は再読み込みしてください）',
  [ERROR_CODES.MEDIA_ROOFER_FIELD_NOT_UPDATABLE]: 'この項目は部分更新では変更できません',
  [ERROR_CODES.MEDIA_ROOFER_IMAGE_URL_UNKNOWN]:
    '画像URLは画像キャビネットに登録済みのものだけ指定できます',
  [ERROR_CODES.MEDIA_ROOFER_NAME_REQUIRED]: '店名は必須です',
  [ERROR_CODES.MEDIA_ROOFER_SLUG_LOCKED]:
    '個別ページを公開中の認定店のslugは変更できません。先にAdmin画面で個別ページを非公開にしてください',
  [ERROR_CODES.MEDIA_ROOFER_CATEGORY_LOCKED]:
    '掲載中または個別ページを公開中の認定店では、分類（やねプロ / とそプロ / いえサプリ導入）は変更できません。先にAdmin画面で掲載をやめ、個別ページを非公開にしてください',
  [ERROR_CODES.MEDIA_ROOFER_COMPANY_NOT_FOUND]: '指定されたいえサプリ企業が見つかりません',
  [ERROR_CODES.MEDIA_ROOFER_INTERVIEW_NOT_FOUND]: '指定されたインタビューが見つかりません',
  [ERROR_CODES.MEDIA_ROOFER_FEATURED_REVIEW_UNKNOWN]:
    '代表口コミは取得済みの口コミから選んでください',

  // ナレッジベース管理関連
  // 文言は go/app_error/error_codes.go の errorMessages と揃えること
  [ERROR_CODES.KNOWLEDGE_SECTION_CONFLICT]:
    '他の管理者が先にこの節を更新しました。最新の内容を確認してから再度保存してください',
  [ERROR_CODES.KNOWLEDGE_SECTION_ORDER_CONFLICT]:
    '他の管理者が先に構成を変更しました。画面を再読み込みしてから並び替えをやり直してください',

  // AI 再実行の回数制限関連
  // 文言は go/app_error/error_codes.go の errorMessages と揃えること
  [ERROR_CODES.MASK_REGENERATION_LIMIT_EXCEEDED]:
    'この写真のこの部位は、マスクの作り直しが上限回数に達しました。うまく調整できない場合はサポートへお問い合わせください',
  [ERROR_CODES.AREA_REANALYZE_LIMIT_EXCEEDED]:
    'この図面は、画像解析のやり直しが上限回数に達しました。範囲の手動調整と面積の計算は引き続き行えます。解析がうまくいかない場合はサポートへお問い合わせください',

  // 契約管理（Admin・新体系）関連
  // 文言は go/app_error/errors.go の sentinel error（ErrActiveContractExists 等）と揃えること
  [ERROR_CODES.CONTRACT_NOT_FOUND]: '指定された契約が見つかりません',
  [ERROR_CODES.CONTRACT_ACTIVE_EXISTS]: '同じプラン種別の有効な契約が既に存在します',
  [ERROR_CODES.CONTRACT_NOT_ACTIVE]: '対象の契約は有効ではありません',
  [ERROR_CODES.CONTRACT_PLAN_TYPE_MISMATCH]: 'プラン種別をまたぐプラン変更はできません',
  [ERROR_CODES.CONTRACT_PLAN_UNCHANGED]: '変更後の内容が現在の契約と同じです',
  [ERROR_CODES.PLAN_NOT_AVAILABLE]: '指定されたプランは新規契約に利用できません',
  [ERROR_CODES.STAFF_LIMIT_EXCEEDED]: 'アカウント上限に達しているため、この操作はできません',
  [ERROR_CODES.STAFF_ARCHIVED]: '停止済のスタッフには、この操作はできません',
  [ERROR_CODES.STAFF_NOT_ARCHIVED]: '停止していないスタッフには、この操作はできません',
  [ERROR_CODES.STAFF_ANONYMIZED]: '匿名化済みのスタッフは停止を解除できません',
  [ERROR_CODES.SUPPORT_STAFF_EMAIL_INVALID]:
    'サポートアカウントにできるのは@h-scape.comのメールアドレスのスタッフだけです',
  [ERROR_CODES.STAFF_NOT_SUPPORT]: 'サポートアカウントではないスタッフには、この操作はできません',
  [ERROR_CODES.STAFF_PLAN_ASSIGNMENT_INVALID]: '所属プランの指定が不正です',

  // プラン申し込み（企業画面）関連
  [ERROR_CODES.PLAN_INQUIRY_RATE_LIMITED]:
    '送信が続けて行われました。しばらく待ってからもう一度お試しください',

  // クレジット台帳関連
  // 消費量（required）がmetadataに載っていれば `formatErrorMessage` が数字入りの文言にする。
  // ここはmetadataが無いときのフォールバック。
  [ERROR_CODES.INSUFFICIENT_CREDIT]:
    'クレジット残高が不足しています。クレジットを追加すると実行できます',

  // クレジット台帳（枠押さえ・確定・解放の失敗の区分。決定104）関連
  // 文言はgo/app_error/error_codes.goのErrorCode定義と揃えること
  [ERROR_CODES.CREDIT_REQUEST_CONFLICT]:
    'この操作は別の内容で既に受け付けられています。画面を更新してやり直してください',
  [ERROR_CODES.CREDIT_REQUEST_CANCELED]: 'この実行は取り消されています。最初からやり直してください',
  [ERROR_CODES.CREDIT_LEDGER_BUSY]: '混み合っています。時間をおいてお試しください',

  // LINE ログイン・連携関連
  [ERROR_CODES.LINE_NOT_LINKED]:
    'このLINEアカウントは連携されていません。いえサプリのアカウントでログインすると連携できます',
  [ERROR_CODES.LINE_ALREADY_LINKED_OTHER]: 'このLINEアカウントは既に別のスタッフと連携されています',
  [ERROR_CODES.LINE_LOGIN_STATE_INVALID]:
    '不正なリクエストです。お手数ですが最初からやり直してください',
  [ERROR_CODES.LINE_LOGIN_SESSION_EXPIRED]:
    'セッションの有効期限が切れました。お手数ですが最初からやり直してください',
  [ERROR_CODES.LINE_API_ERROR]: 'LINEとの通信に失敗しました。時間をおいて再度お試しください',
  [ERROR_CODES.LINE_LOGIN_RATE_LIMITED]:
    'リクエストが多すぎます。しばらく待ってから再度お試しください',
  [ERROR_CODES.LINE_LOGIN_CRM_REQUIRED]:
    'LINEでのログインは業務管理をご利用のスタッフだけが使えます。メールアドレスとパスワードでログインしてください',
  [ERROR_CODES.STAFF_INACTIVE]: 'このスタッフアカウントは停止されています',

  // 流量制限
  [ERROR_CODES.RATE_LIMITED]: 'アクセスが集中しています。しばらく待ってからもう一度お試しください',

  // システムエラー
  [ERROR_CODES.INTERNAL_SERVER_ERROR]: 'システムエラーが発生しました。管理者にお問い合わせください',
  [ERROR_CODES.SERVICE_UNAVAILABLE]:
    'サービスが一時的に利用できません。しばらくしてから再度お試しください',
  [ERROR_CODES.EXTERNAL_SERVICE_ERROR]: '外部サービスとの通信に失敗しました',
}

/**
 * エラーコードに基づいてユーザーフレンドリーなメッセージを取得
 */
export function getErrorMessage(errorCode: string | undefined): string {
  if (!errorCode) {
    return 'エラーが発生しました'
  }

  // 外部からの任意のエラーコードに対応するため型アサーションを使用
  return (ERROR_MESSAGES as Record<string, string>)[errorCode] || 'エラーが発生しました'
}

/**
 * エラーコードとメタデータからフォーマットされたメッセージを生成
 */
export function formatErrorMessage(errorCode: string, metadata?: Record<string, string>): string {
  switch (errorCode) {
    case ERROR_CODES.DEAL_DUPLICATE_EXTERNAL_ID:
      if (metadata?.deal_id && metadata?.external_id) {
        return `案件ID:${metadata.deal_id}と外部ID:${metadata.external_id}が重複しています。重複登録でないかお確かめください`
      }
      if (metadata?.external_id) {
        return `外部ID:${metadata.external_id}は既に登録されています。重複登録でないかお確かめください`
      }
      return getErrorMessage(errorCode)

    case ERROR_CODES.CUSTOMER_DUPLICATE_EMAIL:
      if (metadata?.email) {
        return `メールアドレス「${metadata.email}」は既に登録されています`
      }
      return getErrorMessage(errorCode)

    case ERROR_CODES.CUSTOMER_DUPLICATE_PHONE:
      if (metadata?.phone) {
        return `電話番号「${metadata.phone}」は既に登録されています`
      }
      return getErrorMessage(errorCode)

    case ERROR_CODES.STAFF_DUPLICATE_EMAIL:
      if (metadata?.email) {
        return `メールアドレス「${metadata.email}」は既に登録されています`
      }
      return getErrorMessage(errorCode)

    // 残高不足。`required`（1回あたりの消費量）はサーバーが常に載せる。
    // Go側の文言（app_error/error_codes.goのErrCodeInsufficientCredit）と同じ文になるように保つ。
    // 残高そのものは出さない: 表示側で0に下げ止めた値と食い違う経路があったため
    // （STG 2026-09-17 CR-24 / CR-25。当時は借越により負の値が発生していた）。
    case ERROR_CODES.INSUFFICIENT_CREDIT:
      if (metadata?.required) {
        return `クレジット残高が不足しています。この操作には${metadata.required}クレジット必要です`
      }
      return getErrorMessage(errorCode)

    default:
      return getErrorMessage(errorCode)
  }
}

/**
 * サーバーがエラーコード（google.rpc.ErrorInfoのreason）を載せているときだけ、
 * 対応する日本語文言を返す。載っていなければ `undefined`（呼び出し側の既定文言を使う）。
 *
 * `catch` で `error.message` をそのまま画面に出すと、Connectのエラーは
 * 「[failed_precondition] required=10 available=-45 floor=-50: credit: insufficient credit balance」
 * のようなサーバー内部の文言になる（STG 2026-09-17の現地調査報告書・AIカラー）。
 * 既定文言を持っている画面は、これで「コードがあるときだけ理由を出す」に寄せられる。
 */
export function knownAppErrorMessage(error: unknown): string | undefined {
  const structured = parseStructuredError(error)
  if ((ERROR_MESSAGES as Record<string, string>)[structured.code] == null) {
    return undefined
  }
  return structured.message
}

/**
 * エラー情報を構造化して返す
 */
export interface StructuredError {
  code: string
  message: string
  traceId?: string
  metadata?: Record<string, string>
  timestamp: string
}

/**
 * サーバー内部エラー / インフラ起因を表す Connect Code（数値 enum）。
 * これらは「予期しないサーバーエラー」であり、rawMessage に SQL エラーやスタックなどの
 * サーバー内部情報が含まれ得るため、error_info（構造化エラーコード）が付いていない場合でも
 * rawMessage をそのままユーザーに表示しない。
 * （エラーハンドリング3原則「サーバー情報の隠蔽」/ issue #2191）
 * 対象外の code（invalid_argument / not_found / already_exists 等）は backend が意図した
 * message を rawMessage に載せているケースがあり得るため従来通りフォールバック表示する。
 */
const SERVER_INTERNAL_CONNECT_CODES: ReadonlySet<Code> = new Set([
  Code.Unknown,
  Code.DeadlineExceeded,
  Code.ResourceExhausted,
  Code.Aborted,
  Code.Internal,
  Code.Unavailable,
  Code.DataLoss,
])

// 上記 code の文字列名（connect の codeToString 相当）。テストのモックや一部経路では
// code が数値ではなく文字列名で来る可能性があるため両対応する。
const SERVER_INTERNAL_CONNECT_CODE_NAMES: ReadonlySet<string> = new Set([
  'unknown',
  'deadline_exceeded',
  'resource_exhausted',
  'aborted',
  'internal',
  'unavailable',
  'data_loss',
])

/**
 * Connect Code がサーバー内部エラーかを判定する。
 * 本番の ConnectError は数値 enum（Code）だが、文字列名で来る経路も許容する。
 */
function isServerInternalConnectCode(code: unknown): boolean {
  if (typeof code === 'number') {
    return SERVER_INTERNAL_CONNECT_CODES.has(code)
  }
  if (typeof code === 'string') {
    return SERVER_INTERNAL_CONNECT_CODE_NAMES.has(code)
  }
  return false
}

/**
 * 通信が途中で切れるなどして、サーバーが処理を終えたかどうかが分からない失敗を表す Connect Code。
 * ゲートウェイの時間切れ（HTTP 502〜504 は Unavailable、Cloudflare の 524 などは Unknown になる）や
 * クライアント側の時間切れ・中断がこれにあたり、サーバーは成功して確定済みのことがある。
 */
const OUTCOME_UNKNOWN_CONNECT_CODES: ReadonlySet<Code> = new Set([
  Code.Unknown,
  Code.DeadlineExceeded,
  Code.Unavailable,
  Code.Canceled,
])

const OUTCOME_UNKNOWN_CONNECT_CODE_NAMES: ReadonlySet<string> = new Set([
  'unknown',
  'deadline_exceeded',
  'unavailable',
  'canceled',
])

/**
 * サーバーの処理結果が分からない失敗かを判定する。「失敗したので〇〇は起きていない」と
 * 言い切る案内（例: 取り込みの失敗でクレジットは消費されない。issue #2965）は、これが真のとき
 * 出さない。サーバーが成功していれば、案内が事実に反するため。
 * `fetch` の通信失敗（`TypeError`）も、リクエストが届いたかどうか分からないので含める。
 */
export function isOutcomeUnknownError(error: unknown): boolean {
  if (error instanceof TypeError) {
    return true
  }
  if (error && typeof error === 'object' && 'code' in error) {
    const code = (error as { code?: unknown }).code
    if (typeof code === 'number') {
      return OUTCOME_UNKNOWN_CONNECT_CODES.has(code)
    }
    if (typeof code === 'string') {
      return OUTCOME_UNKNOWN_CONNECT_CODE_NAMES.has(code)
    }
  }
  return false
}

/**
 * エラーレスポンスから構造化エラー情報を抽出
 */
export function parseStructuredError(error: unknown): StructuredError {
  const timestamp = new Date().toISOString()

  // Connect Error の場合
  if (error && typeof error === 'object' && 'code' in error) {
    const connectError = error as {
      code?: string
      rawMessage?: string
      details?: Array<{
        type?: string
        // connect-es v2: IncomingDetail は value: Uint8Array だが、
        // 旧来の 'error_info' 形式では value がオブジェクトとして来るケースを後方互換で維持する
        value?: Uint8Array | Record<string, unknown>
        // connect-go が JSON エンコーディング時に付与する debug（JsonValue）
        // 実形状: { reason: string; domain: string; metadata: Record<string, string> }
        debug?: unknown
      }>
    }

    // メタデータの抽出を試みる
    let errorCode: string | undefined
    let metadata: Record<string, string> | undefined
    let traceId: string | undefined

    if (connectError.details && Array.isArray(connectError.details)) {
      for (const detail of connectError.details) {
        // connect-es v2 実形状: type="google.rpc.ErrorInfo", value=Uint8Array, debug=JsonObject
        if (detail.type === 'google.rpc.ErrorInfo') {
          const debugObj =
            detail.debug && typeof detail.debug === 'object' && !Array.isArray(detail.debug)
              ? (detail.debug as Record<string, unknown>)
              : undefined
          if (debugObj) {
            errorCode = debugObj['reason'] as string | undefined
            const rawMeta = debugObj['metadata']
            metadata =
              rawMeta && typeof rawMeta === 'object' && !Array.isArray(rawMeta)
                ? (rawMeta as Record<string, string>)
                : undefined
            traceId = metadata?.['trace_id']
            break
          }
        }

        // 後方互換: 旧 'error_info' 形式（value がオブジェクト）
        if (detail.type === 'error_info' && detail.value && !(detail.value instanceof Uint8Array)) {
          const valueObj = detail.value as Record<string, unknown>
          errorCode = valueObj['reason'] as string | undefined
          metadata = valueObj['metadata'] as Record<string, string> | undefined
          traceId = metadata?.['trace_id']
          break
        }
      }
    }

    // エラーコードが見つかった場合
    if (errorCode) {
      // ERROR_MESSAGES マップに登録済みのコードはフォーマット済みメッセージを使う。
      // 未登録コード（将来追加された Go 側エラーコード等）は rawMessage を優先表示し、
      // 有用な日本語文言が汎用メッセージに化けるのを防ぐ（回帰防止）。
      const hasRegisteredMessage = (ERROR_MESSAGES as Record<string, string>)[errorCode] != null
      const isSpecialCase =
        errorCode === ERROR_CODES.DEAL_DUPLICATE_EXTERNAL_ID ||
        errorCode === ERROR_CODES.CUSTOMER_DUPLICATE_EMAIL ||
        errorCode === ERROR_CODES.CUSTOMER_DUPLICATE_PHONE ||
        errorCode === ERROR_CODES.STAFF_DUPLICATE_EMAIL
      const message =
        hasRegisteredMessage || isSpecialCase
          ? formatErrorMessage(errorCode, metadata)
          : connectError.rawMessage || 'エラーが発生しました'
      return {
        code: errorCode,
        message,
        traceId,
        metadata,
        timestamp,
      }
    }

    // エラーコード（error_info）が見つからない場合
    // サーバー内部系 code は rawMessage をそのまま表示せず汎用メッセージにする
    // （SQL / FK 制約違反などサーバー内部情報の露出を防ぐ。issue #2191）
    if (isServerInternalConnectCode(connectError.code)) {
      return {
        code: connectError.code || 'UNKNOWN_ERROR',
        message: 'エラーが発生しました',
        timestamp,
      }
    }

    // それ以外の code は従来通り rawMessage をフォールバック表示する
    return {
      code: connectError.code || 'UNKNOWN_ERROR',
      message: connectError.rawMessage || 'エラーが発生しました',
      timestamp,
    }
  }

  // 通常のエラーオブジェクトの場合
  if (error instanceof Error) {
    return {
      code: 'UNKNOWN_ERROR',
      message: error.message || 'エラーが発生しました',
      timestamp,
    }
  }

  // その他の場合
  return {
    code: 'UNKNOWN_ERROR',
    message: 'エラーが発生しました',
    timestamp,
  }
}
